<template>
    <div
        class="otp-input-c2p" 
        :class="disableElements? 'otp-input-c2p--disabled': null" 
        ref="rootElement"
    >
        <src-otp-input 
            display-remember-me="false"
            auto-submit="true"
            :hide-loader="isSuccessful"
            :card-brands="cardBrands"
            :locale="locale"
            :otp-resend-loading="otpResendLoading?'true':'false'"
            :network-id="networkName"
            :is-successful="isSuccessful"
            :disable-elements="disableElements"
            :masked-identity-value="maskedIdentityValue"
            :error-reason="error"
        />

        <div class="otp-input-c2p__remember-me">
            <CieloCheckBoxV2
                :value="rememberMe"
                @input="(value) => $emit('remember-me', value)"
                :clickable-slot="true"
                :text-aria="$ml.get('rememberCards')"
            >
                <span v-text="$ml.get('rememberCards')" />
            </CieloCheckBoxV2>

            <InfoTooltip
                :items="['rememberCardsTooltipText1', 'rememberCardsTooltipText2']"
                :orientation="isMobile ? 'left' : 'top'"
            />
        </div>
    </div>
</template>

<script>
import CieloCheckBoxV2 from '@/components/ClickToPay/template/CieloCheckBoxV2';
import InfoTooltip from '@/components/InfoTooltip';
import OTPStyle from './OneTimePasswordC2PStyles';

export default {
    components: {
        CieloCheckBoxV2,
        InfoTooltip
    },

    data() {
        return {
            innerRememberMeAgreement: false,
            initialized: false,
        };
    },

    computed: {
        email() {
            if(!this.supportedValidationChannels || !this.supportedValidationChannels.length === 0) {
                return '';
            }

            const emailValidationChannel = this.supportedValidationChannels
                .find(channel => channel.identityType === 'EMAIL');

            if(!emailValidationChannel) {
                return '';
            }

            return emailValidationChannel.maskedValidationChannel;
        },
    },

    props: {
        cardBrands: {
            type: Array,
            default: () => []
        },
        maskedIdentityValue: {
            type: String,
            default: ''
        },
        supportedValidationChannels: {
            type: Array,
            default() {
                return [];
            }
        },
        otpResendLoading: {
            type: Boolean,
            default: false
        },
        networkName: {
            type: String,
            default: 'Mastercard'
        },
        locale: {
            type: String,
            default: 'pt_BR'
        },
        error: {
            type: String,
            default: ''
        },
        isSuccessful: {
            type: Boolean,
            default: false
        },
        disableElements: {
            type: Boolean,
            default: false
        },
        rememberMe: {
            type: Boolean,
            default: false
        },
        isMobile: {
            type: Boolean,
            default: false
        }
    },

    watch: {
        supportedValidationChannels(validationChannels) {
            this.initializeComponent(validationChannels);
        }
    },

    mounted() {
        this.initializeComponent(this.supportedValidationChannels);
    },

    methods: {
        async initializeComponent(validationChannels) {
            let srcElement = this.$refs.rootElement.querySelector('src-otp-input');
            const loadOurStyle = () => {
                const extraSheet = new CSSStyleSheet();
                const shadowElement = srcElement.shadowRoot;
                extraSheet.replaceSync(OTPStyle);
                shadowElement.adoptedStyleSheets.push(extraSheet);
                setTimeout(() => {
                    const secondShadowRoot = shadowElement.querySelector('sal-code-input').shadowRoot;
                    secondShadowRoot.adoptedStyleSheets.push(extraSheet);
                    
                }, 100);
            };

            
            if(!this.initialized) {
                // WebComponent pode demorar para carregar.
                await customElements.whenDefined('src-otp-input');

                // Emitted when the user selects alternate validation methods
                srcElement.addEventListener('alternateRequested', (event) => {
                    this.$emit('alternate-requested', event.detail);
                });
                // Emitted when the user clicks the continue button; also emits if auto-submit is enabled 
                // and the OTP input has been completely filled
                srcElement.addEventListener('continue', () => {
                    this.$emit('continue');
                });
                // Emitted when the user click on Not you? link
                srcElement.addEventListener('notYouRequested', () => {
                    this.$emit('not-you-requested');
                });
                // Emitted when a valid OTP is entered
                srcElement.addEventListener('otpChanged', (event) => {
                    this.$emit('otp-changed', event.detail);
                });
                // Emitted when the Remember Me checkbox is selected
                srcElement.addEventListener('rememberMe', (event) => {
                    this.$emit('remember-me', event.detail);
                });
                // Emitted when user requests to resend (EMAIL | SMS | '') OTP
                srcElement.addEventListener('resendOtp', (event) => {
                    this.$emit('resend-otp', event.detail);
                });

                this.initialized = true;
            }
            srcElement.loadSupportedValidationChannels(validationChannels);
            loadOurStyle();
        },
    }
};
</script>

<style lang="scss" scoped>
    .otp-input-c2p {
        display: flex;
        flex-direction: column;
        margin: auto;

        &__remember-me {
            display: flex;
            gap: 8px;
            margin-top: 20px;
        }
    }

    .otp-title {
        text-align: center;
        padding-bottom: 24px;
    }

    .otp-mark {
        padding-bottom: 16px;
    }

    .otp-description {
        padding-bottom: 32px;
    }

    .otp-wrong-email {
        font-size: 14px;
        color: #017CEB;
        text-decoration: underline;
        font-weight: bold;
        padding-bottom: 40px;
        cursor: pointer;
    }

    .otp-inputs {
        display: flex;
        justify-content: center;
        gap: 8px;
        padding-bottom: 16px;
    }

    .otp-input {
        width: 40px;
        height: 48px;
        border-radius: 8px;
        border: 1px solid #C5CED7;
        text-align: center;
        font-size: 24px;
    }

    .otp-input:valid {
        border-color: #017CEB !important;
    }

    .otp-input:focus {
        border-color: #017CEB !important;
    }

    .otp-inputs--success .otp-input {
        border-color: #017CEB !important;
    }

    .otp-error {
        color: #DC392A;
        font-size: 12px;
        text-align: center;
        padding: 0 16px 16px 16px;
        min-height: 12px;
        box-sizing: content-box;
        visibility: hidden;
    }

    .otp-error[visible] {
        visibility: visible;
    }

    .otp-resend {
        font-weight: bold;
        color: #017CEB;
        text-decoration: underline;
        text-align: center;
        font-size: 14px;
        padding-bottom: 40px;
        cursor: pointer;
    }

    .otp-input-c2p--disabled .otp-wrong-email,
    .otp-input-c2p--disabled .otp-resend {
        color: #C5CED7;
    }
</style>